import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { Question } from '../types'
import {
  formatProbability,
  formatEdge,
  formatTimeAgo,
  getEdgeClass,
  truncate,
  formatTimeCountdown,
  getTimeCountdownClass,
  formatSpread,
  getSpreadClass,
  calculateOpportunityScore,
} from '../utils/format'
import clsx from 'clsx'

interface QuestionCardProps {
  question: Question
  onRunPrediction: () => void
}

export function QuestionCard({ question, onRunPrediction }: QuestionCardProps) {
  const prob = question.latest_consensus?.post_market_prob
  const marketProb = question.market?.prob
  const edge = question.edge
  const confidence = question.latest_consensus?.confidence

  const hasSummary = question.has_summary
  const hasPosition = question.has_position

  // Calculate opportunity score
  const spreadBps = question.market?.spread_bps
  const opportunityScore = calculateOpportunityScore(
    edge,
    question.liquidity,
    confidence,
    spreadBps
  )

  // Time countdown with urgency
  const timeCountdown = formatTimeCountdown(question.close_ts)

  return (
    <Link
      to={`/question/${question.id}`}
      className="block glass-card p-3 sm:p-4 hover:border-signal-blue/40 active:bg-void-100/50 transition-all duration-200 group"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-body font-medium text-star text-sm leading-snug line-clamp-2 group-hover:text-signal-blue transition-colors">
            {question.title}
          </h3>
          {question.category && (
            <span className="text-[10px] sm:text-xs text-star-muted font-mono mt-1 inline-block">
              {question.category}
            </span>
          )}
        </div>

        {/* Status badges and opportunity score */}
        <div className="flex flex-col items-end gap-0.5 sm:gap-1 shrink-0">
          {/* Opportunity score badge */}
          {opportunityScore !== null && opportunityScore >= 50 && (
            <span
              className={clsx(
                'text-[10px] font-mono px-1.5 py-0.5 rounded',
                opportunityScore >= 70
                  ? 'bg-signal-green/10 text-signal-green border border-signal-green/30'
                  : 'bg-signal-blue/10 text-signal-blue border border-signal-blue/30'
              )}
              title={`Opportunity Score: ${opportunityScore}/100 (based on edge, liquidity, confidence, spread)`}
            >
              ⚡ {opportunityScore}
            </span>
          )}
          {hasPosition && (
            <span className="badge-purple text-[10px]">◈ Position</span>
          )}
          {hasSummary && (
            <span className="badge-blue text-[10px] hidden sm:inline-flex">📝 Summary</span>
          )}
          {/* Time countdown for closing soon */}
          {timeCountdown.urgency === 'critical' && (
            <span className={clsx('text-[10px] font-mono', getTimeCountdownClass(timeCountdown.urgency))}>
              ⏰ {timeCountdown.text}
            </span>
          )}
        </div>
      </div>
      
      {/* Probability display */}
      <div className="mb-3 sm:mb-4">
        {/* Show analyzed outcome for event questions only when we have a prediction */}
        {question.analyzed_outcome_name && question.run_count > 0 && (
          <div className="mb-2 text-[10px] sm:text-xs text-star-muted font-mono">
            <span className="text-signal-blue">▸</span> {truncate(question.analyzed_outcome_name, 50)}
          </div>
        )}

        <div className="flex items-end justify-between mb-2 gap-2">
          <div className="flex-1">
            <span className="data-label text-[9px] sm:text-xs">Our Estimate</span>
            <div className="data-value-lg text-lg sm:text-2xl">
              {formatProbability(prob)}
            </div>
          </div>

          <div className="text-right">
            <span className="data-label text-[9px] sm:text-xs">Market</span>
            <div className="data-value text-sm sm:text-base">
              {formatProbability(marketProb)}
            </div>
          </div>

          <div className="text-right">
            <span className="data-label text-[9px] sm:text-xs">Edge</span>
            <div className={clsx('data-value font-semibold text-sm sm:text-base', getEdgeClass(edge))}>
              {formatEdge(edge)}
            </div>
          </div>
        </div>
        
        {/* Probability bar */}
        <div className="prob-bar">
          {/* Market probability indicator */}
          {marketProb !== undefined && (
            <div
              className="absolute h-full w-0.5 bg-star-muted/50 z-10"
              style={{ left: `${marketProb * 100}%` }}
            />
          )}
          {/* Our probability bar */}
          <motion.div
            className={clsx(
              'prob-bar-fill',
              prob !== undefined && edge !== undefined
                ? edge > 0.02
                  ? 'bg-gradient-to-r from-signal-green/50 to-signal-green'
                  : edge < -0.02
                    ? 'bg-gradient-to-r from-signal-red/50 to-signal-red'
                    : 'bg-gradient-to-r from-star-muted/50 to-star-muted'
                : 'bg-star-dim'
            )}
            initial={{ width: 0 }}
            animate={{ width: prob !== undefined ? `${prob * 100}%` : '50%' }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
      </div>
      
      {/* Liquidity/Volume/Spread row */}
      {(question.liquidity || question.volume_24h || spreadBps) && (
        <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-[10px] sm:text-xs text-star-dim font-mono mb-2 sm:mb-3">
          {question.liquidity && (
            <span title="Total Value Locked">
              💰 ${(question.liquidity / 1000).toFixed(0)}k
            </span>
          )}
          {question.volume_24h && (
            <span title="24h Volume" className="hidden xs:inline">
              📊 ${(question.volume_24h / 1000).toFixed(0)}k vol
            </span>
          )}
          {spreadBps !== undefined && spreadBps !== null && (
            <span
              className={getSpreadClass(spreadBps)}
              title="Bid-ask spread"
            >
              ↔ {formatSpread(spreadBps)}
            </span>
          )}
        </div>
      )}
      
      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] sm:text-xs text-star-muted font-mono">
        <div className="flex items-center gap-2 sm:gap-3">
          <span>{question.run_count} runs</span>
          {question.latest_consensus?.ran_at && (
            <span className="hidden xs:inline">Last: {formatTimeAgo(question.latest_consensus.ran_at)}</span>
          )}
        </div>

        <button
          className="px-2 py-1.5 sm:py-1 rounded bg-signal-green/10 text-signal-green border border-signal-green/30 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity min-h-[32px] sm:min-h-0"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onRunPrediction()
          }}
        >
          ▶ Run
        </button>
      </div>

      {/* Closing time with urgency indicator */}
      {question.close_ts && (
        <div className="mt-2 pt-2 border-t border-star-dim/20 text-[10px] sm:text-xs font-mono flex items-center justify-between">
          <span className="text-star-dim">Closes:</span>
          <span className={clsx(getTimeCountdownClass(timeCountdown.urgency))}>
            {timeCountdown.text}
            {timeCountdown.urgency === 'critical' && ' ⚠️'}
            {timeCountdown.urgency === 'warning' && ' ⏳'}
          </span>
        </div>
      )}
    </Link>
  )
}


